import type { UploadFileStatus } from "@/lib/storage/upload-manifest-store";

/**
 * The single lifecycle shared by both a clip and its session, mirroring the
 * backend `Phase`. A clip moves through it as it is uploaded, analyzed (per-clip
 * surfer detect/track/embed), and finally grouped (the session-wide cluster
 * step). A session's phase is its "furthest behind" clip, so a session is only
 * as advanced as its slowest clip.
 */
export type Phase =
  | "upload_pending"
  | "upload"
  | "upload_failed"
  | "processing_pending"
  | "processing"
  | "processing_failed"
  | "grouping_pending"
  | "grouping"
  | "grouping_failed"
  | "complete"
  | "failed";

const ALL_PHASES: readonly Phase[] = [
  "upload_pending",
  "upload",
  "upload_failed",
  "processing_pending",
  "processing",
  "processing_failed",
  "grouping_pending",
  "grouping",
  "grouping_failed",
  "complete",
  "failed",
];

// Rank along the happy-path pipeline, used to derive a session's phase from its
// clips. Kept in sync with the backend `PHASE_RANK`.
const PHASE_RANK: Record<string, number> = {
  upload_pending: 0,
  upload: 1,
  processing_pending: 2,
  processing: 3,
  grouping_pending: 4,
  grouping: 5,
  complete: 6,
};
const RANK_TO_PHASE: Record<number, Phase> = {
  0: "upload_pending",
  1: "upload",
  2: "processing_pending",
  3: "processing",
  4: "grouping_pending",
  5: "grouping",
  6: "complete",
};

// Per-clip failures don't stall the session: a failed clip is "settled" and is
// simply excluded from grouping, so for deriving the session phase it counts as
// having reached grouping_pending.
const CLIP_FAILED_PHASES: ReadonlySet<Phase> = new Set([
  "upload_failed",
  "processing_failed",
]);

/** Validate a raw server string into a Phase, falling back to upload_pending. */
export function parsePhase(raw: string | null | undefined): Phase {
  if (raw && (ALL_PHASES as readonly string[]).includes(raw)) {
    return raw as Phase;
  }
  return "upload_pending";
}

/**
 * Derive a session's phase from its clips: the least-advanced (slowest) clip.
 * Per-clip failures are treated as settled (ready to group). When every clip
 * failed before grouping, the session as a whole has failed. Mirrors the
 * backend `furthest_behind`.
 */
export function furthestBehind(phases: Phase[]): Phase {
  if (phases.length === 0) return "upload_pending";

  const ranks = phases.map((phase) =>
    CLIP_FAILED_PHASES.has(phase)
      ? PHASE_RANK.grouping_pending
      : (PHASE_RANK[phase] ?? PHASE_RANK.grouping_pending),
  );
  const derived = RANK_TO_PHASE[Math.min(...ranks)];
  if (
    derived === "grouping_pending" &&
    phases.every((phase) => CLIP_FAILED_PHASES.has(phase))
  ) {
    return "failed";
  }
  return derived;
}

/** Map a client-side upload status to its equivalent Phase. */
export function uploadStatusToPhase(status: UploadFileStatus): Phase {
  switch (status) {
    case "pending":
      return "upload_pending";
    case "uploading":
      return "upload";
    case "uploaded":
      return "processing_pending";
    case "failed":
      return "upload_failed";
  }
}

/**
 * The clip phase to show on the upload screen, combining the client's own
 * upload status with the server's reported phase. The client is authoritative
 * while uploading; the server takes over (processing, grouping) once the upload
 * has landed.
 */
export function mergeClipPhase(
  uploadStatus: UploadFileStatus,
  serverPhase: Phase | undefined,
): Phase {
  if (uploadStatus === "failed") return "upload_failed";
  if (uploadStatus !== "uploaded") return uploadStatusToPhase(uploadStatus);
  return serverPhase ?? "processing_pending";
}

/** Whether a clip's raw upload has finished (so it has a playable original). */
export function isClipUploaded(phase: Phase): boolean {
  return (
    phase !== "upload_pending" &&
    phase !== "upload" &&
    phase !== "upload_failed"
  );
}

/** Whether a clip's per-clip analysis has settled (analyzed or failed). */
export function isClipAnalyzed(phase: Phase): boolean {
  return (
    phase === "processing_failed" ||
    phase === "grouping_pending" ||
    phase === "grouping" ||
    phase === "grouping_failed" ||
    phase === "complete"
  );
}

/** Phases in the upload stage (still uploading, or a failed upload to retry). */
export function isUploadingPhase(phase: Phase): boolean {
  return phase === "upload_pending" || phase === "upload" || phase === "upload_failed";
}

/** Phases where the upload is genuinely in flight (drives auto-resume). */
export function isActivelyUploadingPhase(phase: Phase): boolean {
  return phase === "upload_pending" || phase === "upload";
}

/** Post-upload work is underway (analysis or grouping), but not yet finished. */
export function isProcessingOngoingPhase(phase: Phase): boolean {
  return (
    phase === "processing_pending" ||
    phase === "processing" ||
    phase === "grouping_pending" ||
    phase === "grouping"
  );
}

/** A session has reached a terminal phase. */
export function isTerminalPhase(phase: Phase): boolean {
  return phase === "complete" || phase === "grouping_failed" || phase === "failed";
}

export type PhaseColor =
  | "gray"
  | "blue"
  | "red"
  | "indigo"
  | "violet"
  | "green";

export type PhaseDisplay = {
  label: string;
  color: PhaseColor;
  /** Whether to animate the progress bar to signal ongoing work. */
  active: boolean;
};

/** Label, color, and activity for a phase, shared across every screen. */
export function phaseDisplay(phase: Phase): PhaseDisplay {
  switch (phase) {
    case "upload_pending":
      return { label: "Pending upload", color: "gray", active: false };
    case "upload":
      return { label: "Uploading", color: "blue", active: true };
    case "upload_failed":
      return { label: "Upload failed", color: "red", active: false };
    case "processing_pending":
      return { label: "Pending processing", color: "gray", active: false };
    case "processing":
      return { label: "Processing", color: "indigo", active: true };
    case "processing_failed":
      return { label: "Processing failed", color: "red", active: false };
    case "grouping_pending":
      return { label: "Ready to group", color: "violet", active: false };
    case "grouping":
      return { label: "Grouping", color: "violet", active: true };
    case "grouping_failed":
      return { label: "Grouping failed", color: "red", active: false };
    case "complete":
      return { label: "Complete", color: "green", active: false };
    case "failed":
      return { label: "Failed", color: "red", active: false };
  }
}

/** Plain-language explanation of a phase, shown in a status badge tooltip. */
export function phaseTooltip(phase: Phase): string {
  switch (phase) {
    case "upload_pending":
      return "Waiting in line to start uploading.";
    case "upload":
      return "Your clip is uploading right now.";
    case "upload_failed":
      return "The upload didn't finish. Retry to send it again.";
    case "processing_pending":
      return "Uploaded and waiting to start processing.";
    case "processing":
      return "Finding each surfer in your clips.";
    case "processing_failed":
      return "We couldn't finish processing this clip.";
    case "grouping_pending":
      return "Ready to be grouped with the rest of the clips.";
    case "grouping":
      return "Grouping your clips by surfer.";
    case "grouping_failed":
      return "We couldn't group these clips.";
    case "complete":
      return "All done. Your clips are grouped and ready to watch.";
    case "failed":
      return "Something went wrong with this session.";
  }
}

/** One-line detail shown while a session is still working, by phase. */
export function phaseProgressDetail(phase: Phase): string {
  if (phase === "processing_pending" || phase === "processing") {
    return "Finding each surfer in your clips.";
  }
  if (phase === "grouping_pending" || phase === "grouping") {
    return "Grouping your clips by surfer.";
  }
  return "";
}
